import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { AddressAutocomplete } from 'components/AddressAutocomplete'
import type { AddressAutocompleteHandle, AddressResult } from 'components/AddressAutocomplete'
import ImagePickerPopup from 'components/popups/ImagePickerPopup'
import { Building2, ChevronLeft, ImageIcon, Map as MapIcon, X } from 'lucide-react'
import { cn } from 'lib/utils'
import { blankNoAutofillFields, noAutofillProps } from 'lib/noAutofill'
import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'
import { createSite, updateSite } from 'slices/evaluationSiteSlice'
import { MapPointPicker } from 'components/MapPointPicker'
import type { CapturedSiteFields } from 'components/MapPointPicker'

interface EvaluationSiteFormProps {
	open: boolean
	onClose: () => void
	evalId: string
	site?: any
}

/**
 * Which pane of the dialog is showing.
 *
 * The image picker is a MODE rather than a nested Dialog on purpose:
 * `components/ui/dialog` sets `document.body.style.overflow = 'hidden'` on
 * open and resets it to '' on cleanup, so closing an inner dialog restores
 * background scrolling while the outer one is still up. Keeping exactly one
 * Dialog on screen sidesteps that.
 */
type FormMode = 'form' | 'map' | 'image'

const EvaluationSiteForm = ({ open, onClose, evalId, site }: EvaluationSiteFormProps) => {
	const dispatch = useDispatch()
	const isEdit = Boolean(site)

	const [mode, setMode] = useState<FormMode>('form')
	const [name, setName] = useState('')
	const [address, setAddress] = useState('')
	const [city, setCity] = useState('')
	const [state, setState] = useState('')
	const [postalCode, setPostalCode] = useState('')
	const [propertyType, setPropertyType] = useState('')
	const [sqft, setSqft] = useState('')
	const [latitude, setLatitude] = useState<string>('')
	const [longitude, setLongitude] = useState<string>('')
	const [imageUrl, setImageUrl] = useState('')
	const [googlePlaceId, setGooglePlaceId] = useState<string>('')
	const [submitting, setSubmitting] = useState(false)
	const formRef = useRef<HTMLFormElement>(null)
	const searchRef = useRef<AddressAutocompleteHandle>(null)

	useEffect(() => {
		if (open) {
			setMode('form')
			setName(site?.name || '')
			setAddress(site?.address || '')
			setCity(site?.city || '')
			setState(site?.state || '')
			setPostalCode(site?.postalCode || '')
			setPropertyType(site?.propertyType || '')
			setSqft(site?.sqft != null ? String(site.sqft) : '')
			setLatitude(site?.latitude != null ? String(site.latitude) : '')
			setLongitude(site?.longitude != null ? String(site.longitude) : '')
			setImageUrl(site?.imageUrls?.[0] || '')
			setGooglePlaceId(site?.googlePlaceId || '')
			setSubmitting(false)
		}
	}, [open, site])

	const handleAddressSelect = useCallback(async (result: AddressResult) => {
		const street = [result.address.house_number, result.address.road]
			.filter(Boolean)
			.join(' ')
			.trim()
		setName((prev) => (prev || result.name || ''))
		setAddress(street)
		setCity(result.address.city || '')
		setState(result.address.state || '')
		setPostalCode(result.address.postcode || '')
		setLatitude(String(result.latitude))
		setLongitude(String(result.longitude))
		if (result.placeId) setGooglePlaceId(result.placeId)

		// Auto-pull a Places photo on selection, the same way Add Location
		// does. The URL Google hands back is short-lived, so it is uploaded to
		// Cloudinary immediately rather than stored as-is. Failures are silent:
		// the user can still pick one by hand, and a site with no photo is a
		// perfectly valid site.
		if (result.photoUrl) {
			try {
				const upload = await uploadToCloudinary(result.photoUrl, { resourceType: 'image' })
				setImageUrl(upload.secure_url)
			} catch (err) {
				console.warn('[EvaluationSiteForm] Auto photo upload failed', err)
			}
		}
	}, [])

	/**
	 * Write a map capture into the form and return to it. Same field rules
	 * as an autocomplete pick, including keeping a name the user has already
	 * typed rather than overwriting it with whatever is nearest the pin.
	 */
	const handleCapture = useCallback((captured: CapturedSiteFields) => {
		setLatitude(String(captured.latitude))
		setLongitude(String(captured.longitude))
		if (captured.name) setName((prev) => prev || captured.name || '')
		if (captured.address) setAddress(captured.address)
		if (captured.city) setCity(captured.city)
		if (captured.state) setState(captured.state)
		if (captured.postalCode) setPostalCode(captured.postalCode)
		if (captured.googlePlaceId) setGooglePlaceId(captured.googlePlaceId)
		setMode('form')
	}, [])

	// Reset the form to its initial open state. In add mode that's empty;
	// in edit mode it's the pristine `site` values (mirrors the useEffect).
	const handleClear = useCallback(() => {
		// Blank the DOM before React does, and clear the search box without
		// remounting it. Both are triggers for Chromium's "Update address?"
		// bubble; see `lib/noAutofill`.
		blankNoAutofillFields(formRef.current)
		searchRef.current?.clear()
		setName(site?.name || '')
		setAddress(site?.address || '')
		setCity(site?.city || '')
		setState(site?.state || '')
		setPostalCode(site?.postalCode || '')
		setPropertyType(site?.propertyType || '')
		setSqft(site?.sqft != null ? String(site.sqft) : '')
		setLatitude(site?.latitude != null ? String(site.latitude) : '')
		setLongitude(site?.longitude != null ? String(site.longitude) : '')
		setImageUrl(site?.imageUrls?.[0] || '')
		setGooglePlaceId(site?.googlePlaceId || '')
	}, [site])

	const handleSubmit = useCallback(async () => {
		if (!name.trim()) return
		setSubmitting(true)
		const payload: any = {
			name: name.trim(),
			address: address.trim() || undefined,
			city: city.trim() || undefined,
			state: state.trim() || undefined,
			postalCode: postalCode.trim() || undefined,
			propertyType: propertyType.trim() || undefined,
			sqft: sqft ? Number(sqft) : undefined,
			latitude: latitude ? Number(latitude) : undefined,
			longitude: longitude ? Number(longitude) : undefined,
			googlePlaceId: googlePlaceId || undefined,
			// An empty array, not undefined: the update endpoint skips fields
			// that arrive undefined, so sending undefined would make "Remove"
			// silently no-op on an existing site.
			imageUrls: imageUrl ? [imageUrl] : [],
		}
		try {
			if (isEdit) {
				await (dispatch as any)(updateSite({ evalId, siteId: site._id, payload }))
			} else {
				await (dispatch as any)(createSite({ evalId, payload }))
			}
			onClose()
		} finally {
			setSubmitting(false)
		}
	}, [
		dispatch,
		evalId,
		site,
		isEdit,
		name,
		address,
		city,
		state,
		postalCode,
		propertyType,
		sqft,
		latitude,
		longitude,
		googlePlaceId,
		imageUrl,
		onClose,
	])

	const imageSearchQuery = [name, address, city, state].filter(Boolean).join(' ')

	return (
		<Dialog open={open} onClose={onClose}>
			{/* Wider in map/image mode: a 700px map is too cramped to place a
			    pin accurately. */}
			<DialogContent className={mode === 'form' ? 'max-w-[700px]' : 'max-w-[920px]'}>
				<DialogHeader>
					<div className="flex items-center justify-between gap-3">
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<Building2 className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>{isEdit ? 'Edit Site' : 'New Site'}</DialogTitle>
								<DialogDescription>
									{isEdit
										? 'Update site details.'
										: 'Add a candidate site to this evaluation. Search for it, or place it on the map.'}
								</DialogDescription>
							</div>
						</div>

						{/* Form/Map switch. The panes are rendered conditionally
						    rather than kept mounted, so the map is torn down in
						    form mode instead of holding a WebGL context. */}
						{mode !== 'image' && (
							<div className="flex flex-shrink-0 items-center rounded-lg bg-gray-100 p-0.5">
								{(['form', 'map'] as const).map((paneMode) => (
									<button
										key={paneMode}
										type="button"
										onClick={() => setMode(paneMode)}
										className={cn(
											'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
											mode === paneMode
												? 'bg-white text-[#0080fe] shadow-sm'
												: 'text-gray-500 hover:text-gray-700',
										)}
									>
										{paneMode === 'form' ? (
											<Building2 className="h-3.5 w-3.5" />
										) : (
											<MapIcon className="h-3.5 w-3.5" />
										)}
										{paneMode === 'form' ? 'Form' : 'Map'}
									</button>
								))}
							</div>
						)}
					</div>
				</DialogHeader>

				<div className="p-5">
					{mode === 'map' && (
						<MapPointPicker
							initialLatitude={latitude ? Number(latitude) : undefined}
							initialLongitude={longitude ? Number(longitude) : undefined}
							onCapture={handleCapture}
							captureLabel="Capture Site"
						/>
					)}

					{mode === 'image' && (
						<div className="space-y-4">
							<Button type="button" variant="ghost" onClick={() => setMode('form')} className="gap-2">
								<ChevronLeft className="h-4 w-4" />
								Back to form
							</Button>
							<ImagePickerPopup
								placeId={googlePlaceId || null}
								searchQuery={googlePlaceId ? null : imageSearchQuery || null}
								locationName={name}
								onImageSelected={(url: string) => {
									setImageUrl(url)
									setMode('form')
								}}
							/>
						</div>
					)}

					{/*
						The form stays mounted in the map and image panes and is
						hidden with CSS instead. Unmounting it would remove a filled
						address form from the DOM, which Chromium reads as a
						submission. The MAP is still torn down conditionally above,
						so nothing holds a WebGL context it is not using.
					*/}
					<form
						ref={formRef}
						onSubmit={(e) => {
							e.preventDefault()
							handleSubmit()
						}}
						className={cn('space-y-4', mode !== 'form' && 'hidden')}
						autoComplete="off"
					>
						{/* Guide text */}
						{!isEdit && (
							<p className="text-xs italic text-[#0080fe]">
								In the search box below type in an address, business name, or area. Once you've found your desired site, select it from the dropdown. No address yet? Switch to Map and drop a pin.
							</p>
						)}

						{/* Row 1: Search (add mode only) + Property Type */}
						<div className="grid grid-cols-12 gap-3">
							{!isEdit && (
								<div className="col-span-8">
									<Label className="mb-1.5 block">Search Address</Label>
									<AddressAutocomplete
										ref={searchRef}
										placeholder="Search for a site..."
										onSelect={handleAddressSelect}
									/>
								</div>
							)}
							<div className="col-span-4">
								<Label htmlFor="propertyType" className="mb-1.5 block">Property Type</Label>
								<Input
									id="propertyType"
									value={propertyType}
									onChange={(e) => setPropertyType(e.target.value)}
									placeholder="e.g. Retail, Office"
									{...noAutofillProps}
								/>
							</div>
						</div>

						{/* Row 2: Site Name + Address */}
						<div className="grid grid-cols-12 gap-3">
							<div className="col-span-6">
								<Label htmlFor="siteName" className="mb-1.5 block">Site Name *</Label>
								<Input
									id="siteName"
									value={name}
									onChange={(e) => setName(e.target.value)}
									{...noAutofillProps}
								/>
							</div>
							<div className="col-span-6">
								<Label htmlFor="addr" className="mb-1.5 block">Address</Label>
								<Input
									id="addr"
									value={address}
									onChange={(e) => setAddress(e.target.value)}
									{...noAutofillProps}
								/>
							</div>
						</div>

						{/* Row 3: City + State + Postal Code */}
						<div className="grid grid-cols-12 gap-3">
							<div className="col-span-4">
								<Label htmlFor="city" className="mb-1.5 block">City</Label>
								<Input
									id="city"
									value={city}
									onChange={(e) => setCity(e.target.value)}
									{...noAutofillProps}
								/>
							</div>
							<div className="col-span-4">
								<Label htmlFor="state" className="mb-1.5 block">State/Prov</Label>
								<Input
									id="state"
									value={state}
									onChange={(e) => setState(e.target.value)}
									{...noAutofillProps}
								/>
							</div>
							<div className="col-span-4">
								<Label htmlFor="postalCode" className="mb-1.5 block">Postal Code</Label>
								<Input
									id="postalCode"
									value={postalCode}
									onChange={(e) => setPostalCode(e.target.value)}
									{...noAutofillProps}
								/>
							</div>
						</div>

						{/* Row 4: Latitude + Longitude (set by search or map capture) + SQFT */}
						<div className="grid grid-cols-12 gap-3">
							<div className="col-span-4">
								<Label htmlFor="lat" className="mb-1.5 block">Latitude</Label>
								<Input
									id="lat"
									value={latitude}
									onChange={(e) => setLatitude(e.target.value)}
									disabled={!isEdit}
									{...noAutofillProps}
								/>
							</div>
							<div className="col-span-4">
								<Label htmlFor="lng" className="mb-1.5 block">Longitude</Label>
								<Input
									id="lng"
									value={longitude}
									onChange={(e) => setLongitude(e.target.value)}
									disabled={!isEdit}
									{...noAutofillProps}
								/>
							</div>
							<div className="col-span-4">
								<Label htmlFor="sqft" className="mb-1.5 block">SQFT</Label>
								<Input
									id="sqft"
									type="number"
									value={sqft}
									onChange={(e) => setSqft(e.target.value)}
									{...noAutofillProps}
								/>
							</div>
						</div>

						{/* Image preview, centred, matching Add Location. */}
						{imageUrl && (
							<div className="flex justify-center">
								<img
									className="max-h-48 rounded-lg border border-gray-200 object-cover"
									src={imageUrl}
									alt={name}
								/>
							</div>
						)}

						{/*
							Everything below the inputs is centred, and the image
							controls read the same as Add Location's.

							The one deliberate difference: a photo never gates
							submit here. A pin dropped on a raw parcel has no photo
							to find, and those are the sites this dialog exists for.
						*/}
						<div className="flex flex-col items-center gap-2 pt-2">
							<div className="flex items-center gap-3">
								<Button
									type="button"
									variant="primary"
									isOutline
									onClick={() => setMode('image')}
									className="gap-2"
								>
									<ImageIcon className="w-4 h-4" />
									{imageUrl ? 'Change Image' : 'Add Image'}
								</Button>
								{imageUrl && (
									<Button
										type="button"
										variant="ghost-red"
										className="gap-1.5"
										onClick={() => setImageUrl('')}
									>
										<X className="h-3.5 w-3.5" />
										Remove
									</Button>
								)}
							</div>

							<p className="text-xs italic text-[#0080fe]">
								Select 'Add Image' to import an image for this site. Optional.
							</p>

							<div className="flex items-center gap-3">
								<Button type="button" variant="ghost" onClick={handleClear}>
									Clear
								</Button>
								<Button
									type="submit"
									variant="primary"
									className="gap-2"
									disabled={!name.trim() || submitting}
								>
									<Building2 className="w-4 h-4" />
									{submitting ? 'Saving...' : isEdit ? 'Update Site' : 'Add Site'}
								</Button>
							</div>
						</div>
					</form>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default EvaluationSiteForm
